import React, { useEffect, useState } from "react";
import { fetchHomepage } from "../api/marketingApi";

// Public privacy policy for PropertyMakers (/privacypolicy). Content is static;
// the business contact details are pulled from the configurable homepage content
// so they stay in sync with the rest of the site.
export function MarketingPrivacy() {
  const [contact, setContact] = useState(null);

  useEffect(() => {
    let alive = true;
    fetchHomepage().then((d) => alive && setContact(d?.contact || null)).catch(() => {});
    return () => { alive = false; };
  }, []);

  const email = contact?.email;
  const phone = contact?.phone;
  const address = contact?.address;
  const updated = "September 2026";

  return (
    <section className="mx-auto max-w-[52rem] px-6 pb-24 pt-12 font-sans text-[#4A473E]">
      <span className="text-[0.72rem] font-semibold uppercase tracking-[0.16em] text-[#B7975A]">Legal</span>
      <h1 className="mb-2 mt-2 font-serif text-[clamp(2rem,5vw,2.8rem)] font-semibold tracking-tight text-[#14130F] [font-family:'Iowan_Old_Style','Palatino_Linotype',Palatino,Georgia,serif]">
        Privacy Policy
      </h1>
      <p className="text-[0.9rem] text-[#78746A]">Last updated: {updated}</p>

      <div className="mt-8 space-y-6 text-[1rem] leading-relaxed">
        <p>
          PropertyMakers (“we”, “us”, “our”) is a real-estate brokerage based in Kalyan,
          Maharashtra. This policy explains what personal information we collect when you use
          our website, how we use it, and the choices you have. By using this website or
          submitting an enquiry, you agree to this policy.
        </p>

        <Section title="Information we collect">
          <ul className="list-disc space-y-1.5 pl-5">
            <li><strong>Details you give us</strong> — your name, phone number, and email address when you submit an enquiry, request a quote, or contact us by call, WhatsApp, or form.</li>
            <li><strong>Property interest</strong> — the listing or property type you enquired about, so we can respond usefully.</li>
            <li><strong>Usage data</strong> — pages viewed, referrer, and approximate device/browser information collected automatically to improve the site.</li>
          </ul>
        </Section>

        <Section title="How we use your information">
          <ul className="list-disc space-y-1.5 pl-5">
            <li>To respond to your enquiry and share property options, pricing, and site-visit details.</li>
            <li>To contact you by phone, WhatsApp, SMS, or email about properties relevant to your request.</li>
            <li>To operate, maintain, and improve our website and services.</li>
            <li>To comply with applicable legal obligations.</li>
          </ul>
        </Section>

        <Section title="Consent to be contacted">
          <p>
            When you submit an enquiry you confirm that you consent to being contacted by us
            regarding your request, including via call, WhatsApp, SMS, or email — even if your
            number is registered under DND. You may withdraw this consent at any time (see
            “Your choices” below).
          </p>
        </Section>

        <Section title="Cookies &amp; analytics">
          <p>
            We use cookies and similar technologies for essential site functionality and to
            understand how visitors use the site. We also use the Meta (Facebook) Pixel to
            measure the effectiveness of our advertising — it records actions such as page
            views and enquiries so we can improve our campaigns. You can control cookies
            through your browser settings; blocking them may affect some features.
          </p>
        </Section>

        <Section title="Sharing your information">
          <p>
            We do not sell your personal information. We may share it with trusted service
            providers who help us operate the website and communicate with you (for example,
            messaging and analytics providers), and where required by law. These providers may
            process data on platforms including Meta.
          </p>
        </Section>

        <Section title="Data retention">
          <p>
            We keep enquiry and contact information for as long as needed to serve you and for
            our legitimate business and legal purposes, after which it is deleted or anonymised.
          </p>
        </Section>

        <Section title="Your choices &amp; rights">
          <ul className="list-disc space-y-1.5 pl-5">
            <li>Ask us to stop contacting you at any time.</li>
            <li>Request access to, correction of, or deletion of the personal information we hold about you.</li>
          </ul>
          <p className="mt-2">To exercise any of these, contact us using the details below.</p>
        </Section>

        <Section title="Contact us">
          <p>For any questions about this policy or your data:</p>
          <ul className="mt-2 space-y-1">
            <li><strong>PropertyMakers</strong></li>
            {address && <li>{address}</li>}
            {phone && <li>Phone: <a href={`tel:${phone}`} className="text-[#2C4A87] hover:underline">{phone}</a></li>}
            {email && <li>Email: <a href={`mailto:${email}`} className="text-[#2C4A87] hover:underline">{email}</a></li>}
          </ul>
        </Section>

        <p className="pt-2 text-[0.9rem] text-[#78746A]">
          We may update this policy from time to time. The “last updated” date above reflects the
          most recent change.
        </p>
      </div>
    </section>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <h2 className="mb-2 text-[1.25rem] font-semibold text-[#14130F]">{title}</h2>
      {children}
    </div>
  );
}
